import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import api from "../../services/api";
import { teamQueries, vendorQueries, settingsQueries, aiGuardQueries } from "../../lib/queries";
import { useAuth } from "../../context/AuthContext";
import { useTheme as useCustomTheme } from "../../context/ThemeContext";
import { T, sans, card, topBtn, linkBtn } from "./VendorDetails";
import { VendorLogo } from "../VendorRequests/VendorRequests";
import { BulkImportModal } from "./Vendors";
import PhoneNumberInput from "../../components/PhoneNumberInput";
import CommaNumberInput from "../../components/CommaNumberInput";

/* ── Types ─────────────────────────────────────────────────────────── */
interface FormState {
  name: string; domain: string; category: string; business_units: string[]; data_sensitivity: string; pii_record_volume: string;
  location: string; parent_company: string; vendor_since: string; owner_user_id: string; business_owner: string;
  contract_start_date: string; contract_end_date: string; contract_value: string; auto_renew: boolean; renewal_notice_days: number;
  ytd_spend: string; contact_name: string; contact_email: string; contact_phone: string;
}
interface ExistingVendor { id: string; name: string; domain: string }
interface ExtraContact { key: number; name: string; role: string; email: string; phone: string }

/* ── Constants ─────────────────────────────────────────────────────── */
const OTHER_CATEGORY = '__other__';
const DEFAULT_CATEGORIES = ["Cloud Services", "Technology Services", "Logistics", "Healthcare", "Data & Analytics",
  "Telecommunications", "HR Services", "IT Services", "Finance", "Legal", "Marketing", "Security Services"];
const DEFAULT_BUSINESS_UNITS = ["IT", "Operations", "HR", "Marketing", "Finance", "Legal", "Sales", "Admin",
  "Security", "Compliance", "Privacy", "Tech", "Engineering", "Product"];
const RENEWAL_NOTICE_OPTIONS = [30, 60, 90, 120];
// Same data-sensitivity scale and tier mapping as Pre Onboarding (services/risk_tiering.py), so a vendor
// added directly here still gets the same inherent-risk classification a screened one would.
const SENSITIVITY: Record<string, string> = {
  none: 'No sensitive data', internal: 'Internal data only', pii: 'Customer PII',
  financial: 'Financial or payment data', regulated: 'Regulated (health, financial, etc.)',
};
// Asked only when the vendor handles customer PII: how many records it processes a year (bands, as in
// services/risk_tiering.py PII_RECORD_VOLUME_OPTIONS). Recorded for context; it doesn't change the tier or the score.
export const PII_RECORD_VOLUMES: [string, string][] = [
  ['lt_1k', 'Fewer than 1,000'], ['1k_10k', '1,000 – 10,000'], ['10k_100k', '10,000 – 100,000'],
  ['100k_1m', '100,000 – 1 million'], ['gt_1m', 'More than 1 million'], ['unknown', "Don't know yet"],
];
// The real, possibly tenant-customized mapping (Settings -> Vendor Risk Tiering) is fetched below; this is only the
// fallback shown for the instant it takes to load, and matches the platform default exactly.
const TIER_BY_SENSITIVITY: Record<string, string> = { none: 'Low', internal: 'Medium', pii: 'High', financial: 'High', regulated: 'Critical' };
const TIER_COLOR: Record<string, { fg: string; bg: string; darkFg: string }> = {
  Critical: { fg: '#B91C1C', bg: '#FEE2E2', darkFg: '#F87171' },
  High: { fg: '#C2410C', bg: '#FFEDD5', darkFg: '#FB923C' },
  Medium: { fg: '#B45309', bg: '#FEF3C7', darkFg: '#FBBF24' },
  Low: { fg: '#15803D', bg: '#DCFCE7', darkFg: '#4ADE80' },
};
const EMPTY_MEMBERS: { id: string; name: string; email: string }[] = [];
const EMPTY_FORM: FormState = {
  name: "", domain: "", category: "", business_units: [], data_sensitivity: "none", pii_record_volume: "",
  location: "", parent_company: "", vendor_since: "", owner_user_id: "", business_owner: "",
  contract_start_date: "", contract_end_date: "", contract_value: "", auto_renew: false, renewal_notice_days: 30,
  ytd_spend: "", contact_name: "", contact_email: "", contact_phone: "",
};

/* 'https://Nimbus.io/pricing?x=1' -> 'nimbus.io'. Mirrors backend/routes/vendor_request_routes.py's own
   _normalize_domain, so what the user sees previewed here is what actually gets scanned. */
const normalizeDomain = (value: string) =>
  value.trim().toLowerCase().replace(/^[a-z][a-z0-9+.-]*:\/\//, '').split(/[/?#]/)[0].split('@').pop()!.split(':')[0].replace(/\.$/, '');
// 'cloudcore-data.io' -> 'Cloudcore Data' -- a reasonable starting guess, never shown as more than a draft.
const guessNameFromDomain = (domain: string) => {
  const label = domain.split('.')[0] || '';
  return label.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim();
};
const apiError = (error: any, fallback: string) => {
  const d = error?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x: any) => x?.msg).filter(Boolean).join('; ') || fallback;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  return fallback;
};

const Icon = ({ d, size = 14, style }: { d: string; size?: number; style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, ...style }}><path d={d} /></svg>
);
const I = {
  check: 'M5 12l5 5L20 7', alert: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
  plus: 'M12 5v14M5 12h14',
  back: 'M19 12H5M12 19l-7-7 7-7',
};

export default function AddVendor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant, user } = useAuth();

  // Arriving from an approved pre-onboarding request: pre-fill name/domain, and after a successful
  // onboarding link this vendor back to that request so it drops off the Onboarding Requests tab.
  const fromRequest = location.state as { requestId?: string; name?: string; domain?: string } | null;
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';

  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM, name: fromRequest?.name || "", domain: fromRequest?.domain || "" });
  const nameTouched = useRef(!!fromRequest?.name);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // "Other" in the Category list: the vendor gets whatever is typed here instead.
  const [customCategory, setCustomCategory] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);
  // Extra contacts beyond the primary one, added after the vendor is created (POST /vendors/{id}/contacts) --
  // onboarding itself only takes one contact (contact_name/email/phone on the vendor record).
  const [extraContacts, setExtraContacts] = useState<ExtraContact[]>([]);
  const extraContactKey = useRef(0);
  const addExtraContact = () => setExtraContacts(prev => [...prev, { key: extraContactKey.current++, name: '', role: '', email: '', phone: '' }]);
  const updateExtraContact = (key: number, field: keyof ExtraContact, value: string) =>
    setExtraContacts(prev => prev.map(c => c.key === key ? { ...c, [field]: value } : c));
  const removeExtraContact = (key: number) => setExtraContacts(prev => prev.filter(c => c.key !== key));
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => (prev[key] ? { ...prev, [key]: '' } : prev));
  };

  // Vendor limit
  const maxVendors = tenant?.plan?.max_vendors ?? 5;
  const vendorsQuery = useQuery(vendorQueries.list());
  const vendors = (vendorsQuery.data ?? []) as ExistingVendor[];
  const [limitFromServer, setLimitFromServer] = useState(false);
  const vendorLimitReached = limitFromServer || (!success && maxVendors !== -1 && vendors.length >= maxVendors);
  const planStatus = tenant?.plan?.plan_status ?? 'trial';
  const isPaid = tenant?.plan?.plan === 'pro';

  // Dropdown options
  const categoriesQuery = useQuery(vendorQueries.categories());
  const businessUnitsQuery = useQuery(vendorQueries.businessUnits());
  const categories = categoriesQuery.data?.length ? categoriesQuery.data : DEFAULT_CATEGORIES;
  const knownBusinessUnits = businessUnitsQuery.data?.length ? businessUnitsQuery.data : DEFAULT_BUSINESS_UNITS;
  const [customUnit, setCustomUnit] = useState('');
  // Known units plus anything already picked that isn't on the list yet (a custom unit typed just now).
  const businessUnits = useMemo(
    () => Array.from(new Set([...knownBusinessUnits, ...form.business_units])),
    [knownBusinessUnits, form.business_units]
  );
  const addCustomUnit = () => {
    const unit = customUnit.trim();
    if (!unit) return;
    setCustomUnit('');
    if (!form.business_units.some(u => u.toLowerCase() === unit.toLowerCase())) {
      set('business_units', [...form.business_units, unit]);
    }
  };

  // Owner picker -- default to the person adding the vendor, once, on first load only.
  const teamQuery = useQuery(teamQueries.members());
  const teamMembers = teamQuery.data ?? EMPTY_MEMBERS;
  const ownerDefaulted = useRef(false);
  useEffect(() => {
    if (ownerDefaulted.current || !teamQuery.data) return;
    ownerDefaulted.current = true;
    if (user?.id && teamQuery.data.some((m: any) => m.id === user.id)) {
      setForm(prev => (prev.owner_user_id ? prev : { ...prev, owner_user_id: user.id }));
    }
  }, [teamQuery.data, user?.id]);

  // The workspace's renewal-notice default (Settings -> Workflow defaults), applied once unless already changed here.
  const defaultsQuery = useQuery(settingsQueries.defaults());
  const noticeDefaulted = useRef(false);
  useEffect(() => {
    if (noticeDefaulted.current || !defaultsQuery.data) return;
    noticeDefaulted.current = true;
    const days = defaultsQuery.data.values.renewal_notice_days;
    setForm(prev => (prev.renewal_notice_days === EMPTY_FORM.renewal_notice_days ? { ...prev, renewal_notice_days: days } : prev));
  }, [defaultsQuery.data]);

  const cleanDomain = normalizeDomain(form.domain);
  const duplicate = useMemo(() => {
    if (!cleanDomain) return null;
    return vendors.find(v => (v.domain || '').toLowerCase() === cleanDomain) || null;
  }, [vendors, cleanDomain]);
  const tieringQuery = useQuery(settingsQueries.riskTiering());

  // Onboarding is refused server-side without a verified-working AI provider (backend/middleware/
  // ai_guard.py); this is purely so the button can say why up front instead of failing after the
  // form is filled in. The server re-checks independently either way.
  const aiStatus = useQuery(aiGuardQueries.status());
  const aiBlocked = aiStatus.data?.configured === false;
  const sensitivityTiers = tieringQuery.data?.sensitivity_tiers ?? TIER_BY_SENSITIVITY;
  const tier = sensitivityTiers[form.data_sensitivity] || 'Low';

  const onboardMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data } = await api.post('/v1/vendors/onboarding', body);
      // Best-effort follow-ups: the vendor already exists successfully once onboarding returns, so
      // neither failure here is shown as an onboarding failure.
      if (form.data_sensitivity) {
        await api.patch(`/v1/vendors/${data.id}/profile`, {
          data_sensitivity: form.data_sensitivity,
          ...(form.data_sensitivity === 'pii' ? { pii_record_volume: form.pii_record_volume || null } : {}),
        }).catch(() => {});
      }
      if (fromRequest?.requestId) {
        await api.post(`/v1/vendor-requests/${fromRequest.requestId}/link-vendor`, { vendor_id: data.id }).catch(() => {});
      }
      for (const contact of extraContacts) {
        if (!contact.name.trim()) continue;
        await api.post(`/v1/vendors/${data.id}/contacts`, {
          name: contact.name.trim(), role: contact.role.trim() || null,
          email: contact.email.trim() || null, phone: contact.phone.trim() || null,
        }).catch(() => {});
      }
      return data;
    },
    meta: { invalidates: [['vendors'], ['vendor-requests']] },
  });
  const saving = onboardMutation.isPending;

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Enter the vendor\'s name.';
    if (!cleanDomain) next.domain = 'Enter a valid domain.';
    if (!form.category) next.category = 'Choose a category.';
    else if (form.category === OTHER_CATEGORY && !customCategory.trim()) next.category = 'Type the category.';
    if (form.data_sensitivity === 'pii' && !form.pii_record_volume) next.pii_record_volume = 'Choose roughly how many records, or "Don\'t know yet".';
    if (form.business_units.length === 0) next.business_units = 'Pick at least one business unit.';
    if (!form.owner_user_id) next.owner_user_id = 'Choose an owner.';
    if (!form.location.trim()) next.location = "Enter the vendor's location.";
    if (!form.contact_name.trim()) next.contact_name = "Enter the primary contact's name.";
    if (!form.contact_email.trim()) next.contact_email = "Enter the primary contact's email.";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact_email.trim())) next.contact_email = 'Enter a valid email address.';
    extraContacts.forEach((c, i) => {
      if (c.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(c.email.trim())) next[`extra_contact_${i}`] = 'Enter a valid email address.';
    });
    if (form.contract_start_date && form.contract_end_date && form.contract_end_date < form.contract_start_date) next.contract_end_date = "Can't be before the contract start date.";
    setErrors(next);
    const firstKey = Object.keys(next)[0];
    if (firstKey) document.getElementById(`av-${firstKey.replace(/_/g, '-')}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    if (vendorLimitReached) {
      setSubmitError(`Vendor limit reached. Your ${planStatus} plan allows only ${maxVendors} vendors.`);
      return;
    }
    if (duplicate) {
      setSubmitError(`${duplicate.name} is already tracked at this domain.`);
      return;
    }
    if (!validate()) return;

    try {
      const vendor = await onboardMutation.mutateAsync({
        name: form.name.trim(), domain: cleanDomain,
        category: form.category === OTHER_CATEGORY ? customCategory.trim() : form.category, business_units: form.business_units,
        location: form.location.trim() || null, parent_company: form.parent_company.trim() || null,
        vendor_since: form.vendor_since || null, contract_start_date: form.contract_start_date || null,
        contract_end_date: form.contract_end_date || null, contract_value: form.contract_value ? parseFloat(form.contract_value) : null,
        auto_renew: form.auto_renew, renewal_notice_days: form.renewal_notice_days,
        contact_name: form.contact_name.trim() || null, contact_email: form.contact_email.trim() || null,
        contact_phone: form.contact_phone.trim() || null, ytd_spend: form.ytd_spend ? parseFloat(form.ytd_spend) : null,
        owner_user_id: form.owner_user_id || null, business_owner: form.business_owner.trim() || null,
      });
      setSuccess(`${form.name} was added. Starting a security scan now…`);
      setTimeout(() => navigate(`/vendors/${vendor.id}`), 1100);
    } catch (err: any) {
      const msg = apiError(err, 'Could not add this vendor. Try again.');
      if (msg.toLowerCase().includes('limit reached') || msg.toLowerCase().includes('max_vendors')) {
        setSubmitError(`Vendor limit reached. Your ${planStatus} plan allows only ${maxVendors} vendors.`);
        setLimitFromServer(true);
      } else if (msg.toLowerCase().includes('already exists')) {
        setSubmitError(msg);
        vendorsQuery.refetch();
      } else {
        setSubmitError(msg);
      }
    }
  };

  const c = {
    ink: isDark ? '#e2e8f0' : T.ink, muted: isDark ? '#94a3b8' : T.slate, faint: isDark ? '#64748b' : T.muted,
    line: isDark ? '#1f1f1f' : T.bdr, soft: isDark ? '#141414' : T.surface2, surface: isDark ? '#0d0d0d' : T.surface,
    accent: isDark ? '#60A5FA' : T.blue, red: isDark ? '#F87171' : T.red,
  };
  const input = (hasError?: boolean): React.CSSProperties => ({
    width: '100%', boxSizing: 'border-box', padding: '9px 11px', fontSize: 13, fontFamily: sans, color: c.ink,
    background: c.surface, border: `1px solid ${hasError ? c.red : c.line}`, borderRadius: 7, outline: 'none',
  });
  const label: React.CSSProperties = { display: 'block', fontSize: 11.5, fontWeight: 600, color: c.muted, marginBottom: 5 };
  const errorText: React.CSSProperties = { fontSize: 11.5, color: c.red, marginTop: 4 };
  const section = (title: string, subtitle: string, children: React.ReactNode, optional?: boolean) => (
    <div style={{ ...card(isDark), padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: c.ink }}>{title}</div>
        {optional && <span style={{ fontSize: 11, color: c.faint, fontWeight: 600 }}>Optional</span>}
      </div>
      <div style={{ fontSize: 12, color: c.muted, marginBottom: 16 }}>{subtitle}</div>
      {children}
    </div>
  );
  const row2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 };

  const loadingOptions = categoriesQuery.isPending || businessUnitsQuery.isPending;
  if (loadingOptions) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', color: c.muted, fontFamily: sans, fontSize: 13 }}>Loading…</div>;
  }

  return (
    <div style={{ padding: '20px 24px 100px', fontFamily: sans }}>
      <style>{`@media (max-width: 900px) { .av-grid { grid-template-columns: 1fr !important; } }`}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <button onClick={() => navigate('/vendors')} style={{ ...linkBtn(isDark), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Icon d={I.back} size={13} />Back to vendors
        </button>
        {!fromRequest?.requestId && (
          <button
            onClick={() => !vendorLimitReached && setBulkImporting(true)}
            style={{ ...topBtn(false, isDark), opacity: vendorLimitReached ? 0.5 : 1, cursor: vendorLimitReached ? 'not-allowed' : 'pointer' }}
            title={vendorLimitReached ? `Your plan allows ${maxVendors} vendors` : "Add many vendors at once from a CSV"}
            disabled={vendorLimitReached}
          >↑ Bulk import instead</button>
        )}
      </div>

      {/* header with live preview */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
        <VendorLogo name={form.name || cleanDomain || '?'} domain={cleanDomain} size={48} isDark={isDark} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {form.name.trim() || 'Add a vendor'}
          </div>
          <div style={{ fontSize: 13, color: c.muted, marginTop: 1 }}>
            {cleanDomain || "We'll scan this vendor's domain and track their risk going forward."}
          </div>
        </div>
      </div>

      {bulkImporting && (
        <BulkImportModal
          isDark={isDark}
          onClose={() => setBulkImporting(false)}
          onDone={created => {
            setBulkImporting(false);
            if (created > 0) {
              setSuccess(`${created} vendor${created === 1 ? '' : 's'} imported. Taking you to the vendor list…`);
              setTimeout(() => navigate('/vendors'), 1200);
            }
          }}
        />
      )}

      {fromRequest?.requestId && (
        <div style={{ ...card(isDark), padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: c.muted }}>
          <Icon d={I.check} size={14} style={{ color: TIER_COLOR.Low.fg }} />
          From an approved pre-onboarding request — name and domain are pre-filled.
        </div>
      )}

      {vendorLimitReached && !isPaid && (
        <div style={{ ...card(isDark), padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, borderColor: TIER_COLOR.Medium.fg }}>
          <Icon d={I.alert} size={18} style={{ color: TIER_COLOR.Medium.fg, flexShrink: 0 }} />
          <div style={{ flex: 1, fontSize: 12.5, color: c.ink }}>
            <strong>Vendor limit reached.</strong> Your {planStatus} plan allows only {maxVendors} vendors.
          </div>
          <button onClick={() => navigate('/settings')} style={topBtn(true, isDark)}>Upgrade plan</button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {section('Identify the vendor', 'The domain is what gets scanned, so get it right.', (
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={row2}>
              <div>
                <label style={label} htmlFor="av-domain">Domain</label>
                <input id="av-domain" style={input(!!errors.domain || !!duplicate)} value={form.domain} autoFocus maxLength={255} placeholder="nimbusdata.io"
                  onChange={e => {
                    const value = e.target.value;
                    set('domain', value);
                    const derived = guessNameFromDomain(normalizeDomain(value));
                    if (!nameTouched.current && derived) {
                      setForm(prev => ({ ...prev, name: derived }));
                      setErrors(prev => (prev.name ? { ...prev, name: '' } : prev));
                    }
                  }} />
                {form.domain.trim() && cleanDomain !== form.domain.trim() && !errors.domain && (
                  <div style={{ fontSize: 11.5, color: c.faint, marginTop: 4 }}>Will scan <strong style={{ color: c.ink }}>{cleanDomain || '—'}</strong></div>
                )}
                {errors.domain && <div style={errorText}>{errors.domain}</div>}
              </div>
              <div>
                <label style={label} htmlFor="av-name">Vendor name</label>
                <input id="av-name" style={input(!!errors.name)} value={form.name} maxLength={200} placeholder="Nimbus Data Co"
                  onChange={e => { nameTouched.current = true; set('name', e.target.value); }} />
                {errors.name && <div style={errorText}>{errors.name}</div>}
              </div>
            </div>
            {duplicate && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, background: isDark ? 'rgba(248,113,113,.1)' : '#FEF2F2', fontSize: 12.5, color: c.ink }}>
                <Icon d={I.alert} size={15} style={{ color: c.red, flexShrink: 0 }} />
                <span style={{ flex: 1 }}><strong>{duplicate.name}</strong> is already tracked at this domain.</span>
                <button onClick={() => navigate(`/vendors/${duplicate.id}`)} style={{ ...linkBtn(isDark), whiteSpace: 'nowrap' }}>Open it →</button>
              </div>
            )}
          </div>
        ))}

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16, alignItems: 'start' }} className="av-grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
        {section('Classification', 'Drives how this vendor is scored and how often it needs reassessing.', (
          <div style={{ display: 'grid', gap: 16 }}>
            <div style={row2}>
              <div>
                <label style={label} htmlFor="av-category">Category</label>
                <select id="av-category" style={input(!!errors.category)} value={form.category} onChange={e => set('category', e.target.value)}>
                  <option value="">Select a category</option>
                  {categories.map((cat: string) => <option key={cat} value={cat}>{cat}</option>)}
                  <option value={OTHER_CATEGORY}>Other (type your own)</option>
                </select>
                {form.category === OTHER_CATEGORY && (
                  <input id="av-category-other" style={{ ...input(!!errors.category), marginTop: 8 }} value={customCategory} maxLength={100}
                    autoFocus placeholder="e.g. Payroll Services" onChange={e => setCustomCategory(e.target.value)} />
                )}
                {errors.category && <div style={errorText}>{errors.category}</div>}
              </div>
              <div>
                <label style={label} htmlFor="av-sensitivity">Data this vendor handles</label>
                <select id="av-sensitivity" style={input()} value={form.data_sensitivity} onChange={e => set('data_sensitivity', e.target.value)}>
                  {Object.entries(SENSITIVITY).map(([value, lbl]) => <option key={value} value={value}>{lbl}</option>)}
                </select>
                <div style={{ fontSize: 11.5, color: c.muted, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  Inherent tier:
                  <span style={{
                    display: 'inline-flex', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                    color: isDark ? TIER_COLOR[tier].darkFg : TIER_COLOR[tier].fg, background: isDark ? `${TIER_COLOR[tier].fg}26` : TIER_COLOR[tier].bg,
                  }}>{tier}</span>
                </div>
              </div>
            </div>
            {form.data_sensitivity === 'pii' && (
              <div id="av-pii-record-volume">
                <label style={label}>How many customer PII records does this vendor process in a year?</label>
                <div role="radiogroup" aria-label="Customer PII records processed a year" style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {PII_RECORD_VOLUMES.map(([value, text]) => {
                    const on = form.pii_record_volume === value;
                    return (
                      <button key={value} type="button" role="radio" aria-checked={on} onClick={() => set('pii_record_volume', value)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 99, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans,
                          border: `1px solid ${on ? c.accent : (errors.pii_record_volume ? c.red : c.line)}`, background: on ? (isDark ? 'rgba(59,130,246,.16)' : T.blueBg) : c.surface,
                          color: on ? c.accent : c.ink,
                        }}>
                        {on && <Icon d={I.check} size={12} />}{text}
                      </button>
                    );
                  })}
                </div>
                {errors.pii_record_volume
                  ? <div style={errorText}>{errors.pii_record_volume}</div>
                  : <div style={{ fontSize: 11.5, color: c.muted, marginTop: 6 }}>An estimate is fine. It shows how many people would be affected if this vendor had a breach.</div>}
              </div>
            )}
            <div id="av-business-units">
              <label style={label}>Business units using this vendor</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                {businessUnits.map((unit: string) => {
                  const on = form.business_units.includes(unit);
                  return (
                    <button key={unit} type="button" onClick={() => set('business_units', on ? form.business_units.filter(u => u !== unit) : [...form.business_units, unit])}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 99, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans,
                        border: `1px solid ${on ? c.accent : (errors.business_units ? c.red : c.line)}`, background: on ? (isDark ? 'rgba(59,130,246,.16)' : T.blueBg) : c.surface,
                        color: on ? c.accent : c.ink,
                      }}>
                      {on && <Icon d={I.check} size={11} />}{unit}
                    </button>
                  );
                })}
              </div>
              {errors.business_units && <div style={errorText}>{errors.business_units}</div>}
              <div style={{ display: 'flex', gap: 7, marginTop: 10 }}>
                <input
                  style={{ ...input(), flex: '0 1 220px' }} value={customUnit} maxLength={60} placeholder="Add a custom business unit"
                  onChange={e => setCustomUnit(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomUnit(); } }}
                />
                <button type="button" onClick={addCustomUnit} disabled={!customUnit.trim()}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: `1px dashed ${c.line}`, borderRadius: 7,
                    padding: '0 12px', color: customUnit.trim() ? c.accent : c.faint, fontSize: 12.5, fontWeight: 600,
                    cursor: customUnit.trim() ? 'pointer' : 'default', fontFamily: sans,
                  }}>
                  <Icon d={I.plus} size={13} />Add
                </button>
              </div>
            </div>
          </div>
        ))}

        {section('Relationship', 'Who owns this vendor and where they fit in the business.', (
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={row2}>
              <div>
                <label style={label} htmlFor="av-owner">Owner</label>
                <select id="av-owner" style={input(!!errors.owner_user_id)} value={form.owner_user_id} onChange={e => set('owner_user_id', e.target.value)}>
                  <option value="">Select an owner</option>
                  {teamMembers.map((m: any) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                {errors.owner_user_id ? <div style={errorText}>{errors.owner_user_id}</div>
                  : <div style={{ fontSize: 11, color: c.faint, marginTop: 4 }}>The team member accountable for this vendor.</div>}
              </div>
              <div>
                <label style={label} htmlFor="av-business-owner">Business owner</label>
                <input id="av-business-owner" style={input()} list="av-team-names" value={form.business_owner} maxLength={200} placeholder="Pick a teammate or type a name"
                  onChange={e => set('business_owner', e.target.value)} />
                <datalist id="av-team-names">{teamMembers.map((m: any) => <option key={m.id} value={m.name} />)}</datalist>
                <div style={{ fontSize: 11, color: c.faint, marginTop: 4 }}>The business-side stakeholder who uses this vendor.</div>
              </div>
            </div>
            <div style={row2}>
              <div>
                <label style={label} htmlFor="av-location">Location</label>
                <input id="av-location" style={input(!!errors.location)} value={form.location} maxLength={200} placeholder="Austin, Texas, USA" onChange={e => set('location', e.target.value)} />
                {errors.location && <div style={errorText}>{errors.location}</div>}
              </div>
              <div>
                <label style={label} htmlFor="av-parent">Parent company</label>
                <input id="av-parent" style={input()} value={form.parent_company} maxLength={200} placeholder="Nimbus Holdings" onChange={e => set('parent_company', e.target.value)} />
              </div>
            </div>
            <div style={{ width: 'calc(50% - 7px)' }}>
              <label style={label} htmlFor="av-since">Vendor since</label>
              <input id="av-since" type="date" style={input()} value={form.vendor_since} onChange={e => set('vendor_since', e.target.value)} />
            </div>
          </div>
        ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
        {section('Contract & spend', 'Powers renewal reminders on the vendor list.', (
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={row2}>
              <div>
                <label style={label} htmlFor="av-contract-start">Contract start date</label>
                <input id="av-contract-start" type="date" style={input()} value={form.contract_start_date} onChange={e => set('contract_start_date', e.target.value)} />
              </div>
              <div>
                <label style={label} htmlFor="av-contract-end">Contract end date</label>
                <input id="av-contract-end" type="date" style={input(!!errors.contract_end_date)} value={form.contract_end_date} onChange={e => set('contract_end_date', e.target.value)} />
                {errors.contract_end_date && <div style={errorText}>{errors.contract_end_date}</div>}
              </div>
            </div>
            <div style={row2}>
              <div>
                <label style={label} htmlFor="av-contract-value">Contract value ($)</label>
                <CommaNumberInput id="av-contract-value" style={input()} value={form.contract_value} placeholder="2,500,000" onChange={v => set('contract_value', v)} />
              </div>
              <div>
                <label style={label} htmlFor="av-ytd">YTD spend ($)</label>
                <CommaNumberInput id="av-ytd" style={input()} value={form.ytd_spend} placeholder="2,450,000" onChange={v => set('ytd_spend', v)} />
              </div>
            </div>
            <div style={row2}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: c.ink, cursor: 'pointer', marginTop: 20 }}>
                <input type="checkbox" checked={form.auto_renew} onChange={e => set('auto_renew', e.target.checked)} />
                Contract auto-renews
              </label>
              <div>
                <label style={label} htmlFor="av-notice">Renewal notice period</label>
                <select id="av-notice" style={input()} value={form.renewal_notice_days} onChange={e => set('renewal_notice_days', Number(e.target.value))}>
                  {RENEWAL_NOTICE_OPTIONS.map(d => <option key={d} value={d}>{d} days before expiry</option>)}
                </select>
              </div>
            </div>
          </div>
        ), true)}

        {section('Contacts', 'Who to reach at the vendor for security questions. The first one is the primary contact.', (
          <div style={{ display: 'grid', gap: 18 }}>
            <div style={row2}>
              <div>
                <label style={label} htmlFor="av-contact-name">Contact name</label>
                <input id="av-contact-name" style={input(!!errors.contact_name)} value={form.contact_name} maxLength={200} placeholder="Jane Doe" onChange={e => set('contact_name', e.target.value)} />
                {errors.contact_name && <div style={errorText}>{errors.contact_name}</div>}
              </div>
              <div>
                <label style={label} htmlFor="av-contact-email">Contact email</label>
                <input id="av-contact-email" type="email" style={input(!!errors.contact_email)} value={form.contact_email} maxLength={254} placeholder="jane@nimbusdata.io" onChange={e => set('contact_email', e.target.value)} />
                {errors.contact_email && <div style={errorText}>{errors.contact_email}</div>}
              </div>
              <div>
                <label style={label} htmlFor="av-contact-phone">Contact phone</label>
                <PhoneNumberInput id="av-contact-phone" inputStyle={input()} value={form.contact_phone} placeholder="555 0100" onChange={v => set('contact_phone', v)} />
              </div>
            </div>

            {extraContacts.length > 0 && (
              <div style={{ display: 'grid', gap: 12 }}>
                {extraContacts.map((contact, i) => (
                  <div key={contact.key} style={{ padding: '12px 14px', borderRadius: 8, border: `1px solid ${c.line}`, background: c.soft }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: c.muted }}>Additional contact</span>
                      <button type="button" onClick={() => removeExtraContact(contact.key)}
                        style={{ background: 'none', border: 'none', padding: 0, color: c.faint, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>
                        Remove
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
                      <div>
                        <label style={label} htmlFor={`av-extra-name-${contact.key}`}>Name</label>
                        <input id={`av-extra-name-${contact.key}`} style={input()} value={contact.name} maxLength={200} placeholder="John Smith"
                          onChange={e => updateExtraContact(contact.key, 'name', e.target.value)} />
                      </div>
                      <div>
                        <label style={label} htmlFor={`av-extra-role-${contact.key}`}>Role</label>
                        <input id={`av-extra-role-${contact.key}`} style={input()} value={contact.role} maxLength={120} placeholder="IT Manager"
                          onChange={e => updateExtraContact(contact.key, 'role', e.target.value)} />
                      </div>
                      <div>
                        <label style={label} htmlFor={`av-extra-email-${contact.key}`}>Email</label>
                        <input id={`av-extra-email-${contact.key}`} type="email" style={input(!!errors[`extra_contact_${i}`])} value={contact.email} maxLength={254} placeholder="john@nimbusdata.io"
                          onChange={e => updateExtraContact(contact.key, 'email', e.target.value)} />
                        {errors[`extra_contact_${i}`] && <div style={errorText}>{errors[`extra_contact_${i}`]}</div>}
                      </div>
                      <div>
                        <label style={label} htmlFor={`av-extra-phone-${contact.key}`}>Phone</label>
                        <PhoneNumberInput id={`av-extra-phone-${contact.key}`} inputStyle={input()} value={contact.phone} placeholder="555 0100"
                          onChange={v => updateExtraContact(contact.key, 'phone', v)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button type="button" onClick={addExtraContact}
              style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: `1px dashed ${c.line}`, borderRadius: 7, padding: '7px 12px', color: c.accent, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>
              <Icon d={I.plus} size={13} />Add another contact
            </button>
          </div>
        ))}
        </div>
        </div>
      </div>

      {/* sticky action bar */}
      <div style={{
        position: 'sticky', bottom: 0, marginTop: 20, padding: '14px 18px', borderRadius: 10, border: `1px solid ${c.line}`,
        background: c.surface, display: 'flex', alignItems: 'center', gap: 12, boxShadow: isDark ? '0 -4px 16px rgba(0,0,0,.4)' : '0 -4px 16px rgba(15,23,42,.06)',
      }}>
        <div style={{ flex: 1, fontSize: 12, color: success ? TIER_COLOR.Low.fg : (submitError || aiBlocked ? c.red : c.faint) }} role={submitError || aiBlocked ? 'alert' : undefined}>
          {success ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon d={I.check} size={13} />{success}</span>
            : aiBlocked ? (aiStatus.data?.reason || 'An AI provider is required before vendors can be added.')
            : submitError || "A security scan starts automatically once this vendor is added."}
        </div>
        <button onClick={() => navigate('/vendors')} style={topBtn(false, isDark)} disabled={saving}>Cancel</button>
        <button
          onClick={() => {
            if (aiBlocked && aiStatus.data) {
              window.dispatchEvent(new CustomEvent('fluxwatch:ai-blocked', {
                detail: { aiMode: aiStatus.data.ai_mode, message: aiStatus.data.reason || '' },
              }));
              return;
            }
            handleSubmit();
          }}
          style={{ ...topBtn(true, isDark), opacity: saving || vendorLimitReached || aiBlocked ? 0.6 : 1 }}
          disabled={saving || vendorLimitReached || !!success}
          title={aiBlocked ? (aiStatus.data?.reason || undefined) : undefined}
        >
          {saving ? 'Adding…' : 'Add vendor & scan'}
        </button>
      </div>
    </div>
  );
}
